import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { Button, Input, Radio, Icon, Row, Col, Switch } from "antd";
import * as R from "ramda";

import i18n from "../../../../../../i18n";
import {
  Field,
  FieldArray,
  FormConfig
} from "../../../../../controls/FormikForm";
import AnswerInput from "../../inputs/AnswerInput";
import multipleOptions from "../../../../../../assets/chatbot/multipleOptions.svg";
import yesNo from "../../../../../../assets/chatbot/yesNo.svg";
import openQuestion from "../../../../../../assets/chatbot/openQuestion.svg";
import docQuestion from "../../../../../../assets/chatbot/docQuestion.svg";
import CustomButton from "../../../../../../components/Button/Button";

import { leaveInterceptor } from "./selectors";
import UploadDocument from "./UploadDocument";

import styled from "styled-components/macro";

export const ANSWER_TYPES = {
  options: { type: "options", tooltip: true },
  yes_no: { type: "yes_no", tooltip: true },
  open: { type: "open", tooltip: true },
  document: { type: "document", tooltip: true }
};

const ANSWER_IMAGES = {
  options: multipleOptions,
  yes_no: yesNo,
  open: openQuestion,
  document: docQuestion
};

export const DEFAULT_TYPE_ANSWERS = {
  [ANSWER_TYPES.yes_no.type]: [
    {
      message: "",
      answer: "Si",
      tag: ""
    },
    {
      message: "",
      answer: "No",
      tag: ""
    }
  ],
  [ANSWER_TYPES.options.type]: [
    { message: "", answer: "", tag: "" },
    { message: "", answer: "", tag: "" }
  ],
  [ANSWER_TYPES.open.type]: [
    { message: "", answer: "Respuesta abierta", tag: "" }
  ],
  [ANSWER_TYPES.document.type]: [
    { message: "", answer: "Documento adjunto", tag: "" }
  ],
  end: []
};

const answerYesNo = DEFAULT_TYPE_ANSWERS[ANSWER_TYPES.yes_no.type];

const TagIcon = styled(Icon)`
  color: ${({ theme }) => theme["@primary"]} !important;
  font-size: 16px;
  margin-left: 4px;
`;

const Footer = styled.footer`
  margin-top: 24px;
  border-top: ${({ theme }) => `1px solid ${theme["@gray-40"]}`};
  padding-top: 20px;
  display: flex;
  justify-content: space-between;
`;

const Label = styled.h2`
  align-items: center;
  display: flex;
  font-size: 16px;
  line-height: 16px;
  height: 24px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-weight: normal;
  font-size: 14px;
  color: ${({ theme }) => theme["@gray-80"]};
`;

const RadioLabel = styled.p`
  color: ${({ theme, selected }) => (selected ? theme["@primary"] : "inherit")};
  font-weight: bold;
  margin-top: 10px;
  text-align: center;
`;

const OptionCard = styled.div`
  width: 100%;
  margin-left: 5px;
  margin-right: 5px;
  box-shadow: ${({ theme, selected }) =>
    selected && `0 0 2px 2px ${theme["@primary"]}`};
  border: ${({ theme }) => `1px solid ${theme["@gray-40"]}`};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
`;

const Info = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme["@gray-60"]};
  text-align: center;
  margin-top: 10px;
`;

const ErrorMessage = styled.p`
  color: ${({ theme }) => theme["@alert"]};
  font-size: 12px;
  width: 300px;
  margin-left: 20px;
  margin-top: -15px;
`;

const Number = styled.div`
  margin-top: 7px;
  margin-right: 5px;
  min-width: 15px;
  color: ${({ theme }) => theme["@primary"]};
`;

const ImageContent = styled.div`
  background: ${({ theme }) => theme["@gray-20"]};
  height: 58px;
  border-radius: 3px 3px 0 0;
`;

const RadioItem = ({ label, value, tooltip, selectedValue, image }) => {
  const isSelected = useMemo(() => value === selectedValue, [
    selectedValue,
    value
  ]);
  return (
    <OptionCard selected={isSelected}>
      <ImageContent>
        <img src={image} alt={value} />
      </ImageContent>
      <Radio
        data-test={`radio-item-${value}`}
        value={value}
        style={{
          marginTop: 20,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          whiteSpace: "pre-wrap",
          alignItems: "center",
          marginRight: 0
        }}
      >
        <RadioLabel selected={isSelected}>{label}</RadioLabel>
        <Info>{tooltip}</Info>
      </Radio>
    </OptionCard>
  );
};

const validationSchema = Yup.object().shape({
  options: Yup.array()
    .of(
      Yup.object().shape({
        answer: Yup.string().required(i18n.t("form:emptyFieldValidation")),
        message: Yup.string().required(i18n.t("form:emptyFieldValidation")),
        tag: Yup.string()
      })
    )
    .required(i18n.t("form:emptyFieldValidation"))
});

// TODO: Review this function with ramda
const isValid = values => {
  if (R.equals("", values.message)) return false;

  let valid = true;

  values.options.forEach(option => {
    if (R.equals("", option.answer) || R.equals("", option.message)) {
      valid = false;
    }
  });
  return valid;
};

const QuestionForm = ({ t, onSubmit, onClose, message }) => {
  const TITLE_BY_TYPE = useMemo(() => {
    return {
      options: t("opportunity:process.stepTypes.chatbot.tree.expectedAnswer"),
      yes_no: t("opportunity:process.stepTypes.chatbot.tree.answer"),
      open: t("opportunity:process.stepTypes.chatbot.tree.answer"),
      document: t("opportunity:process.stepTypes.chatbot.tree.answer")
    };
  }, [t]);

  const [alert, setAlert] = useState(false);

  function handleSubmit(values) {
    onSubmit(leaveInterceptor(values));
    onClose();
  }

  return (
    <FormConfig
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      initialValues={{
        message,
        type: ANSWER_TYPES.options.type,
        options: DEFAULT_TYPE_ANSWERS[ANSWER_TYPES.options.type]
      }}
    >
      {({ values, handleSubmit, setFieldValue }) => {
        const canDeleteOptions =
          values.options.length > 2 &&
          values.type === ANSWER_TYPES.options.type;

        if (
          values.type === ANSWER_TYPES.options.type &&
          values.options.length === 2 &&
          ((values.options[0].answer === R.toLower(answerYesNo[0].answer) &&
            values.options[1].answer === R.toLower(answerYesNo[1].answer)) ||
            (values.options[0].answer === R.toLower(answerYesNo[1].answer) &&
              values.options[1].answer === R.toLower(answerYesNo[0].answer)))
        ) {
          setAlert(true);
        } else {
          setAlert(false);
        }

        return (
          <form
            data-test="form-add-branch"
            onSubmit={event => {
              event.stopPropagation();
              handleSubmit(event);
            }}
          >
            <h2>
              {t("opportunity:process.stepTypes.chatbot.tree.addMessage")}
            </h2>
            <Field name="message">
              <Input.TextArea
                rows={2}
                placeholder={t("opportunity:screening.question")}
                data-test="question-message"
                style={{ resize: "none" }}
              />
            </Field>
            <h3 style={{ marginTop: 30 }}>
              {t("opportunity:process.stepTypes.chatbot.tree.selectType")}
            </h3>
            <Field
              name="type"
              onChange={type => {
                if (type === ANSWER_TYPES.document.type) {
                  values.uploadDocument = false;
                  values.document = "";
                } else {
                  delete values.uploadDocument;
                  delete values.document;
                }
                setFieldValue("options", DEFAULT_TYPE_ANSWERS[type]);
              }}
            >
              <Radio.Group
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: 10,
                  marginBottom: 10,
                  marginLeft: -5,
                  marginRight: -5
                }}
              >
                {Object.values(R.omit(["document"], ANSWER_TYPES)).map(
                  answer => (
                    <RadioItem
                      selectedValue={values.type}
                      key={answer.type}
                      t={t}
                      value={answer.type}
                      label={t(
                        `opportunity:process.stepTypes.chatbot.tree.types.${answer.type}`
                      )}
                      tooltip={
                        answer.tooltip &&
                        t(
                          `opportunity:process.stepTypes.chatbot.tree.tooltip.${answer.type}`
                        )
                      }
                      image={ANSWER_IMAGES[answer.type]}
                    />
                  )
                )}
              </Radio.Group>
            </Field>
            <Label>
              {t("opportunity:process.stepTypes.chatbot.tree.candidateAnswer")}
            </Label>
            {values.type === "document" && (
              <>
                <Title style={{ paddingLeft: 4 }}>
                  {t(
                    "opportunity:process.stepTypes.chatbot.tree.selectDocument"
                  )}
                </Title>
                <Field name={"document"}>
                  <UploadDocument defaultFilename={values.document} />
                </Field>
              </>
            )}
            <FieldArray
              name="options"
              render={arrayHelpers => (
                <div>
                  <Row
                    gutter={[8, 0]}
                    style={{ marginLeft: values.type === "document" ? 0 : 15 }}
                  >
                    <Col span={8}>
                      <Title>{TITLE_BY_TYPE[values.type]}</Title>
                    </Col>
                    <Col span={canDeleteOptions ? 10 : 12}>
                      <Title>
                        {t(
                          "opportunity:process.stepTypes.chatbot.tree.message"
                        )}
                      </Title>
                    </Col>
                    <Col span={4}>
                      <Title>
                        {t("opportunity:process.stepTypes.chatbot.tree.tag")}
                        <TagIcon type="tag" />
                      </Title>
                    </Col>
                  </Row>
                  {values.options.map((option, index) => (
                    <div style={{ display: "flex" }} key={index}>
                      {values.type !== "document" && (
                        <Number>{index + 1}.</Number>
                      )}
                      <Row gutter={[8, 0]} style={{ width: "100%" }}>
                        <Col span={8}>
                          {values.type === "document" ? (
                            <div style={{ display: "flex" }}>
                              <Field
                                name={"uploadDocument"}
                                valueProp={"checked"}
                              >
                                <Switch />
                              </Field>
                              <Title style={{ marginLeft: 15 }}>
                                {t(
                                  "opportunity:process.stepTypes.chatbot.tree.expectDocument"
                                )}
                              </Title>
                            </div>
                          ) : (
                            <Field name={`options.${index}.answer`}>
                              <AnswerInput
                                answerType={values.type}
                                t={t}
                                data-test={`answer-option-${index}`}
                                disabled={
                                  values.type !== ANSWER_TYPES.options.type
                                }
                                className="answer"
                                placeholder={`${t(
                                  "opportunity:process.stepTypes.chatbot.tree.candidateAnswer"
                                )}`}
                              />
                            </Field>
                          )}
                        </Col>
                        <Col span={canDeleteOptions ? 10 : 12}>
                          <Field name={`options.${index}.message`}>
                            <Input
                              data-test={`message-option-${index}`}
                              className="message"
                              placeholder={`${t(
                                "opportunity:process.stepTypes.chatbot.tree.message"
                              )}`}
                            />
                          </Field>
                        </Col>
                        <Col span={4}>
                          <Field name={`options.${index}.tag`}>
                            <Input
                              data-test={`tag-option-${index}`}
                              className="tag"
                              placeholder={`${t(
                                "opportunity:process.stepTypes.chatbot.tree.tag"
                              )}`}
                            />
                          </Field>
                        </Col>
                        {canDeleteOptions && (
                          <Col span={2}>
                            <Button
                              shape="circle"
                              type="danger"
                              icon="delete"
                              onClick={() => arrayHelpers.remove(index)}
                            />
                          </Col>
                        )}
                      </Row>
                    </div>
                  ))}
                  {values.type === "options" && alert && (
                    <ErrorMessage>
                      {t("opportunity:screening.chatbotTypeOptionAlert")}
                    </ErrorMessage>
                  )}
                  {values.type === "options" && (
                    <Row type="flex" justify="center">
                      <CustomButton
                        icon="plus"
                        onClick={() =>
                          arrayHelpers.push({ message: "", answer: "" })
                        }
                      >
                        {t(
                          "opportunity:process.stepTypes.chatbot.tree.addOption"
                        )}
                      </CustomButton>
                    </Row>
                  )}
                </div>
              )}
            />
            <Footer>
              <CustomButton
                style={{ marginRight: 12 }}
                htmlType="button"
                type="quaternary"
                onClick={onClose}
              >
                {t("global:cancel")}
              </CustomButton>
              <CustomButton
                htmlType="submit"
                type="primary"
                disabled={!isValid(values) || alert}
                data-test="save-button"
              >
                {t("global:save")}
              </CustomButton>
            </Footer>
          </form>
        );
      }}
    </FormConfig>
  );
};

export default QuestionForm;
